import type React from 'react';
import { Helmet } from 'react-helmet-async';

import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_ORIGIN } from '../../constants/site';

interface PageHeadProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  /** For pages with no canonical URL of their own, such as the error page. */
  noIndex?: boolean;
}

export const PageHead: React.FC<PageHeadProps> = ({
  title,
  description,
  path = '',
  image,
  jsonLd,
  noIndex = false,
}) => {
  const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = `${SITE_ORIGIN}${path}`;
  const ogImagePath = image ?? DEFAULT_OG_IMAGE;
  const ogImage = ogImagePath.startsWith('http')
    ? ogImagePath
    : `${SITE_ORIGIN}${ogImagePath}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex ? (
        <meta name="robots" content="noindex" />
      ) : (
        <link rel="canonical" href={canonicalUrl} />
      )}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((item, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(item)}
          </script>
        ))}
    </Helmet>
  );
};
